import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { StorageUsageResponse } from '@services/storage-usage/interfaces/storage-usage.interfaces';
import { StorageUsageService } from '@services/storage-usage/storage-usage.service';
import { formatStorageBytes } from '@shared/utils/catalog-image.utils';

const STORAGE_USAGE_WARNING_PERCENTAGE = 80;
const STORAGE_USAGE_CRITICAL_PERCENTAGE = 95;

@Component({
  selector: 'app-storage-usage-bar',
  standalone: true,
  templateUrl: './storage-usage-bar.component.html',
  styleUrl: './storage-usage-bar.component.css',
})
export class StorageUsageBarComponent implements OnInit {
  public readonly storageUsage = signal<StorageUsageResponse | null>(null);
  public readonly isLoading = signal(true);
  public readonly hasLoadError = signal(false);

  public readonly usedPercentage = computed(() => {
    const storageUsage = this.storageUsage();
    if (!storageUsage || storageUsage.limitBytes <= 0) {
      return 0;
    }

    return Math.min(100, (storageUsage.usedBytes / storageUsage.limitBytes) * 100);
  });

  public readonly usageLevel = computed(() => {
    const usedPercentage = this.usedPercentage();
    if (usedPercentage >= STORAGE_USAGE_CRITICAL_PERCENTAGE) {
      return 'critical';
    }

    return usedPercentage >= STORAGE_USAGE_WARNING_PERCENTAGE ? 'warning' : 'normal';
  });

  public readonly usedLabel = computed(() => formatStorageBytes(this.storageUsage()?.usedBytes ?? 0));
  public readonly limitLabel = computed(() => formatStorageBytes(this.storageUsage()?.limitBytes ?? 0));

  private readonly storageUsageService = inject(StorageUsageService);
  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.loadStorageUsage();
  }

  public loadStorageUsage(): void {
    this.isLoading.set(true);
    this.hasLoadError.set(false);

    this.storageUsageService.getStorageUsage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (storageUsage) => {
          this.storageUsage.set(storageUsage);
          this.isLoading.set(false);
        },
        error: () => {
          this.hasLoadError.set(true);
          this.isLoading.set(false);
        },
      });
  }
}
